import React from 'react';
import type { AlertColor } from '@mui/material';
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Error,
  ErrorOutline,
  ErrorOutlined,
  Info,
  InfoOutlined,
  ReportProblemOutlined,
  Warning,
} from '@mui/icons-material';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import type { ConnectionLogEntry } from 'app/types';
import { formatDateTime } from 'app/utils/formatter/dateTimeFormatter';
import { ActionButton } from '../ActionButton';
import type { PaginationProps } from '../Pagination';
import { Pagination } from '../Pagination';

type InsurerDataLogsAlertProps = {
  logs: ConnectionLogEntry[];
  totalItems: number;
  paginationProps: PaginationProps;
  testId: string;
};

// Same log level thresholds as Connections LogsTable
const getAlertSeverity = (logLevel: number): AlertColor => {
  if (logLevel >= 400) {
    return 'error';
  }
  if (logLevel >= 300) {
    return 'warning';
  }
  return 'info';
};

const ALERT_SEVERITY_ICONS: Record<AlertColor, typeof InfoOutlined> = {
  error: ErrorOutline,
  warning: ReportProblemOutlined,
  info: InfoOutlined,
  success: InfoOutlined,
};

const LogLevelIcon = ({ logLevel }: { logLevel: number }) => {
  if (logLevel >= 500) {
    return <Error color="error" />;
  }
  if (logLevel >= 400) {
    return <ErrorOutlined color="primary" />;
  }
  if (logLevel >= 300) {
    return <Warning color="primary" />;
  }
  if (logLevel > 100) {
    return <Info color="secondary" />;
  }
  return <Info color="disabled" />;
};

export const InsurerDataLogsAlert = ({ logs, totalItems, paginationProps, testId }: InsurerDataLogsAlertProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const [selectedLog, setSelectedLog] = React.useState<ConnectionLogEntry | null>(null);

  const severity = React.useMemo(() => getAlertSeverity(Math.max(...logs.map(log => log.logLevel))), [logs]);
  const SeverityIcon = ALERT_SEVERITY_ICONS[severity];

  if (logs.length === 0) {
    return null;
  }

  return (
    <>
      <Alert
        data-testid={`${testId}-alert`}
        severity={severity}
        icon={false}
        sx={{ '& .MuiAlert-message': { flexGrow: 1 } }}
      >
        {/* Header with its own icon so it stays vertically centered with the title and the toggle button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SeverityIcon fontSize="small" sx={{ color: `${severity}.main` }} />
          <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
            <FormattedMessage id={TranslationKeys.connections_logs_alert_title} values={{ count: totalItems }} />
          </Typography>
          <ActionButton
            testId={`${testId}-toggle`}
            size="small"
            icon={expanded ? faChevronUp : faChevronDown}
            messageId={
              expanded
                ? TranslationKeys['connections_logs_alert_hide-details']
                : TranslationKeys['connections_logs_alert_show-details']
            }
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
          />
        </Box>

        <Collapse in={expanded} unmountOnExit>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage id={TranslationKeys.connections_logs_logType} />
                </TableCell>
                <TableCell>
                  <FormattedMessage id={TranslationKeys.connections_logs_message} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <FormattedMessage id={TranslationKeys.connections_logs_createdAt} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow
                  key={log.id}
                  data-testid={`${testId}-row-${log.id}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLog(log)}
                >
                  <TableCell>
                    <LogLevelIcon logLevel={log.logLevel} />
                  </TableCell>
                  <TableCell>{log.message}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalItems > logs.length && (
            <Box>
              <Pagination {...paginationProps} />
            </Box>
          )}
        </Collapse>
      </Alert>

      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ 'data-testid': `modal-${testId}-detail-container` } as object}
      >
        {selectedLog && (
          <>
            <DialogTitle>{selectedLog.code}</DialogTitle>
            <DialogContent>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id={TranslationKeys.connections_logs_message} />
                    </TableCell>
                    <TableCell>{selectedLog.message}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id={TranslationKeys.connections_logs_alert_detail_level} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LogLevelIcon logLevel={selectedLog.logLevel} />
                        {selectedLog.logLevelName}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id={TranslationKeys.connections_logs_createdAt} />
                    </TableCell>
                    <TableCell>{formatDateTime(selectedLog.createdAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id={TranslationKeys.connections_logs_updatedAt} />
                    </TableCell>
                    <TableCell>{formatDateTime(selectedLog.updatedAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id={TranslationKeys.connections_logs_alert_detail_correlationId} />
                    </TableCell>
                    <TableCell>{selectedLog.correlationId}</TableCell>
                  </TableRow>
                  {selectedLog.solution && (
                    <TableRow>
                      <TableCell>
                        <FormattedMessage id={TranslationKeys.connections_logs_alert_detail_solution} />
                      </TableCell>
                      <TableCell>{selectedLog.solution}</TableCell>
                    </TableRow>
                  )}
                  {selectedLog.wiki && (
                    <TableRow>
                      <TableCell>
                        <FormattedMessage id={TranslationKeys.connections_logs_alert_detail_wiki} />
                      </TableCell>
                      <TableCell>
                        <Link href={selectedLog.wiki} target="_blank" rel="noopener noreferrer">
                          {selectedLog.wiki}
                        </Link>
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedLog.extraData && (
                    <TableRow>
                      <TableCell>
                        <FormattedMessage id={TranslationKeys.connections_logs_alert_detail_extraData} />
                      </TableCell>
                      <TableCell>
                        <pre>{JSON.stringify(selectedLog.extraData, null, 2)}</pre>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <ActionButton
                testId={`modal-${testId}-detail-btn-close`}
                messageId={TranslationKeys.close}
                onClick={() => setSelectedLog(null)}
              />
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
